/**
 * Hook for loading "Me" profile data
 *
 * Consolidates profile picture and user email loading
 * for the MeProfileCard component.
 *
 * Primary source for profile picture is now FamilyMember.profilePicture,
 * falling back to ProfilePictureManager for migration compatibility.
 */

import { useState, useEffect, useCallback } from 'react';
import { ProfilePictureManager } from '@/lib/profile';
import { useSupabaseAuth } from '@/lib/supabase';
import { useFamilyMembers } from '@/hooks/use-family-members';
import { useMeDisplayName } from '@/hooks/use-me-display-name';

type MeProfileData = {
  profilePictureUri: string | null;
  displayName: string;
  email: string | null;
  isLoading: boolean;
};

type UseMeProfileResult = MeProfileData & {
  refresh: () => Promise<void>;
};

export function useMeProfile(): UseMeProfileResult {
  const { session } = useSupabaseAuth();
  const { meMember, isLoading: isFamilyLoading } = useFamilyMembers();
  const [profilePictureUri, setProfilePictureUri] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadProfilePicture = useCallback(async () => {
    // Primary source: FamilyMember.profilePicture
    if (meMember?.profilePicture) {
      setProfilePictureUri(meMember.profilePicture);
      return;
    }

    // Fallback: ProfilePictureManager (for migration)
    try {
      const manager = ProfilePictureManager.getInstance();
      await manager.initialize();
      const uri = manager.getProfilePictureUri();
      setProfilePictureUri(uri);
    } catch (error) {
      console.error('[useMeProfile] Failed to load profile picture:', error);
      setProfilePictureUri(null);
    }
  }, [meMember?.profilePicture]);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    await loadProfilePicture();
    setIsLoading(false);
  }, [loadProfilePicture]);

  // Initial load and updates when meMember changes
  useEffect(() => {
    async function init() {
      if (isFamilyLoading) return;
      setIsLoading(true);
      await loadProfilePicture();
      setIsLoading(false);
    }
    init();
  }, [loadProfilePicture, isFamilyLoading]);

  const { displayName } = useMeDisplayName();

  return {
    profilePictureUri,
    displayName,
    email: session?.user?.email ?? null,
    isLoading: isLoading || isFamilyLoading,
    refresh,
  };
}
